/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import 'bootstrap/dist/css/bootstrap.min.css';
import { Row } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import { useTheme } from 'styled-components';
import { useState, useEffect, useContext } from 'react';
import { getSupercat, Supercat, getSupercatCounts, getSetting } from '../data';
import { StatusContext, StatusContextType } from './StatusContext';
import { SupercatCard } from './SupercatCard';

interface SupercatData extends Supercat {
    categories: number;
    products: number;
    applications: number;
}

type PresentMode = 'none' | 'fill' | 'pause';

function DashSummary({ }) {
    const theme : any = useTheme();
    const statusContext = useContext(StatusContext);
    const [supercats, setSupercats] = useState<SupercatData[]>([]);
    const [loading, setLoading] = useState(true);
    const [presentMode, setPresentMode] = useState<PresentMode>('none');
    const [pauseStage, setPauseStage] = useState(0);

    const textStyle = { color: theme.colors.textMuted };

    useEffect(() => {
        async function fetchSupercats() {
            try {
                let data = await getSupercat();
                const cnts = await getSupercatCounts();
                const presentSetting = await getSetting('/present');
                let enrichedData:SupercatData[] = data.map((supercat) => ({
                    ...supercat,
                    categories: cnts.find((c) => c.supercat === supercat.key)?.category_count || 0,
                    products: cnts.find((c) => c.supercat === supercat.key)?.match_count || 0,
                    applications: cnts.find((c) => c.supercat === supercat.key)?.finger_count || 0,
                })).sort((a, b) => b.products - a.products).filter(sc => sc.categories > 0 || sc.products > 0);
                setSupercats(enrichedData);
                if (presentSetting === 'fill' || presentSetting === 'pause') {
                    setPresentMode(presentSetting);
                } else {
                    setPresentMode('none');
                }
            } catch (error: any) {
                statusContext.postError(`Error fetching supercats: ${error.message}`);
                console.error('Error fetching supercats:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchSupercats();
    }, [statusContext]);

    useEffect(() => {
        if (presentMode !== 'pause') {
            setPauseStage(2);
            return;
        }

        setPauseStage(0);
        const handleAdvance = () => {
            setPauseStage((prev) => Math.min(prev + 1, 2));
        };

        window.addEventListener('fbot:present-advance', handleAdvance);
        return () => {
            window.removeEventListener('fbot:present-advance', handleAdvance);
        };
    }, [presentMode]);

    if (loading) {
        return (
            <Container fluid className="pt-3 px-4">
                <p style={textStyle}>Loading supercats...</p>
            </Container>
        );
    }

    const animationsEnabled = presentMode === 'fill' || presentMode === 'pause';

    // Animation timing: stagger cards so all fill in ~1 second
    const cardAnimDuration = 200; // Individual card drop duration
    const totalAnimTime = 1500; // Total time for all cards to appear
    const staggerDelay = supercats.length > 1 ? totalAnimTime / (supercats.length - 1) : 0;

    // Separate the "ALL" card from others
    const allCard = supercats.find(sc => sc.key === 'ALL');
    const otherCards = supercats.filter(sc => sc.key !== 'ALL');

    // Calculate delays: ALL card in first position but animates last
    const cardsWithDelay: Array<{ supercat: SupercatData; delay: number; isPlaceholder?: boolean }> = [];

    if (presentMode === 'pause' && pauseStage === 0) {
        return (
            <Container fluid className="pt-3 px-4">
                <Row className="g-3" />
            </Container>
        );
    }

    // Add ALL card first (position 0) with the longest delay
    if (allCard) {
        if (presentMode === 'pause' && pauseStage < 2) {
            cardsWithDelay.push({
                supercat: allCard,
                delay: 0,
                isPlaceholder: true
            });
        } else {
            cardsWithDelay.push({
                supercat: allCard,
                delay: presentMode === 'pause' ? 0 : otherCards.length * staggerDelay
            });
        }
    }

    // Add other cards with staggered delays
    otherCards.forEach((supercat, index) => {
        cardsWithDelay.push({ 
            supercat, 
            delay: index * staggerDelay 
        });
    });

    return (
        <Container fluid className="pt-3 px-4">
            <Row className="g-3">
                {cardsWithDelay.map((item) => (
                    <SupercatCard
                        key={item.isPlaceholder ? `${item.supercat.key}-placeholder` : item.supercat.key}
                        supercat={item.supercat}
                        animationDelay={item.delay}
                        animate={animationsEnabled}
                        isPlaceholder={item.isPlaceholder}
                    />
                ))}
            </Row>
        </Container>
    );
}

export default DashSummary;
